import React, { useState } from "react";
import moment from "moment";
import { NavigateFunction } from "react-router-dom";
import {
  androidIcon,
  appleIcon,
  calendarIcon,
  chevronRight,
  closeIcon,
  exclamationIcon,
  laptopIcon,
  macosIcon,
  timeIcon,
  windowsIcon,
} from "../../assets/icons/SvgIconList";
import ICCheckbox from "../../core-component/form/ICCheckbox";
import { ICButton } from "../../core-component/ui";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { cn } from "../../lib";
import { prepareMessageFromParams } from "../../utils/AppFunctions";
import {
  ILoginSessionItem,
  ILoginSessionLimit,
  submitLoginSessionSelect,
} from "./verifyOtpController";
import {
  LOGIN_CLIENT_TYPE,
  SUBMIT_LOGIN_SESSION_SELECT,
} from "../../utils/AppEnumerations";

interface ILoginSessionSelectModalProps {
  sessionLimit: ILoginSessionLimit;
  navigation: NavigateFunction;
  onClose: () => void;
}

const SESSION_DATE_FORMAT = "DD MMM YYYY, hh:mm A";

const DEVICE_OS_ICON: Record<string, React.ReactNode> = {
  Android: androidIcon,
  iOS: appleIcon,
  macOS: macosIcon,
  Windows: windowsIcon,
};

const LoginSessionSelectModal: React.FC<ILoginSessionSelectModalProps> = ({
  sessionLimit,
  navigation,
  onClose,
}) => {
  const { t } = UseTranslationHook();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const requiredCount = Math.max(sessionLimit.required_count, 1);
  const canSubmit = selectedIds.length >= requiredCount && !loading;

  const toggleSession = (sessionId: string) =>
    setSelectedIds((prev) =>
      prev.includes(sessionId)
        ? prev.filter((id) => id !== sessionId)
        : [...prev, sessionId],
    );

  const deviceLabel = (session: ILoginSessionItem) =>
    [
      session.browser ??
        t(`login_client_${session.client_type ?? LOGIN_CLIENT_TYPE.Unknown}`),
      session.os,
    ]
      .filter(Boolean)
      .join(" · ") || t("login_session_unknown_device");

  const handleSubmit = async () => {
    const result = await submitLoginSessionSelect(
      { login_ticket: sessionLimit.login_ticket, session_ids: selectedIds },
      navigation,
      setLoading,
      t,
    );
    if (result === SUBMIT_LOGIN_SESSION_SELECT.Expired) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
      <div className="mx-4 flex max-h-screen w-full max-w-xl flex-col gap-5 rounded-2xl bg-subtle p-6 shadow-lg md:mx-0">
        <div className="flex items-start gap-4">
          <div className="grid size-12 shrink-0 place-items-center rounded-full bg-danger-muted text-danger">
            <span className="size-6">{exclamationIcon}</span>
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-bold text-default">
              {t("login_session_limit_title")}
            </h2>
            <p className="mt-1 text-sm text-muted">
              {prepareMessageFromParams(t("login_session_limit_description"), [
                ["max", String(sessionLimit.max_sessions)],
                ["count", String(requiredCount)],
              ])}
            </p>
          </div>
          <div
            className="bg-transparent size-6  shrink-0 text-accent transition-colors hover:text-default cursor-pointer"
            aria-label={t("login_session_cancel")}
            onClick={onClose}
          >
            {closeIcon}
          </div>
        </div>

        <span className="w-fit rounded-full bg-accent-muted px-4 py-1.5 text-sm font-semibold text-accent">
          {prepareMessageFromParams(t("login_session_selected"), [
            ["selected", String(selectedIds.length)],
            ["count", String(requiredCount)],
          ])}
        </span>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-scroll">
          {sessionLimit.sessions.map((session) => {
            const isSelected = selectedIds.includes(session.id);
            return (
              <div
                key={session.id}
                className={cn(
                  "flex cursor-pointer items-center gap-4 rounded-xl border p-4",
                  {
                    "border-accent bg-accent-muted": isSelected,
                    "border-base": !isSelected,
                  },
                )}
                onClick={() => toggleSession(session.id)}
              >
                <div onClick={(e) => e.stopPropagation()}>
                  <ICCheckbox
                    checked={isSelected}
                    onChange={() => toggleSession(session.id)}
                  />
                </div>
                <div className="grid size-12 shrink-0 place-items-center rounded-full bg-accent-muted text-accent">
                  <span className="size-6">
                    {(session.os && DEVICE_OS_ICON[session.os]) || laptopIcon}
                  </span>
                </div>
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="truncate font-semibold text-default">
                    {deviceLabel(session)}
                  </div>
                  {session.ip_address && (
                    <div className="truncate text-sm text-muted">
                      {session.ip_address}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-muted">
                    <span className="size-4 shrink-0">{calendarIcon}</span>
                    <span className="truncate">
                      {t("login_session_signed_in_at")}:{" "}
                      {moment(session.created_date).format(SESSION_DATE_FORMAT)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted">
                    <span className="size-4 shrink-0">{timeIcon}</span>
                    <span className="truncate">
                      {t("login_session_last_active")}:{" "}
                      {moment(session.last_used_date).format(
                        SESSION_DATE_FORMAT,
                      )}
                    </span>
                  </div>
                </div>
                <span className="size-5 shrink-0 text-muted">
                  {chevronRight}
                </span>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-base pt-4">
          <ICButton
            type="button"
            onClick={onClose}
            className="!w-fit border border-base !bg-transparent !px-6 !text-default"
            children={t("login_session_cancel")}
          />
          <ICButton
            type="button"
            onClick={handleSubmit}
            loading={loading}
            disabled={!canSubmit}
            className={cn("!w-fit !px-6", {
              "cursor-not-allowed opacity-60": !canSubmit,
            })}
            children={t("login_session_sign_out_and_continue")}
          />
        </div>
      </div>
    </div>
  );
};

export default LoginSessionSelectModal;
